import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

const API = '/api';

export default function Fullscreen() {
  const [searchParams] = useSearchParams();
  const [raceData, setRaceData] = useState(null);
  const [gameType, setGameType] = useState("");
  const [legNumber, setLegNumber] = useState(1);
  const [trackName, setTrackName] = useState("");
  const [flashingHorses, setFlashingHorses] = useState(new Set());
  const [currentGameId, setCurrentGameId] = useState(null);
  const [currentLeg, setCurrentLeg] = useState(1);
  
  // Get initial values from URL params or localStorage
  useEffect(() => {
    const urlGameId = searchParams.get("game");
    const urlLeg = parseInt(searchParams.get("leg") || "1", 10);
    
    if (urlGameId) {
      setCurrentGameId(urlGameId);
      setCurrentLeg(urlLeg);
    } else {
      const checkStorage = () => {
        const stored = localStorage.getItem('atg-overlay-selection');
        if (stored) {
          try {
            const selection = JSON.parse(stored);
            if (selection.gameId) {
              setCurrentGameId(selection.gameId);
              setCurrentLeg(selection.leg || 1);
              return true;
            }
          } catch (e) {
            console.error("Failed to parse stored selection", e);
          }
        }
        return false;
      };
      
      if (!checkStorage()) {
        const pollInit = setInterval(() => {
          if (checkStorage()) {
            clearInterval(pollInit);
          }
        }, 200);
        setTimeout(() => clearInterval(pollInit), 30000);
      }
    }
  }, [searchParams]);

  // Listen for localStorage changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'atg-overlay-selection' && e.newValue) {
        try {
          const selection = JSON.parse(e.newValue);
          setCurrentGameId(selection.gameId);
          setCurrentLeg(selection.leg);
        } catch (err) {
          console.error("Failed to parse selection", err);
        }
      }
    };

    const pollInterval = setInterval(() => {
      const stored = localStorage.getItem('atg-overlay-selection');
      if (stored) {
        try {
          const selection = JSON.parse(stored);
          if (selection.gameId !== currentGameId || selection.leg !== currentLeg) {
            setCurrentGameId(selection.gameId);
            setCurrentLeg(selection.leg);
          }
        } catch (e) {}
      }
    }, 500);

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(pollInterval);
    };
  }, [currentGameId, currentLeg]);

  // Fetch race data
  const fetchRaceData = useCallback(async () => {
    if (!currentGameId) return;
    
    try {
      const response = await axios.get(`${API}/race/${currentGameId}/${currentLeg}`);
      const data = response.data;
      
      setGameType(data.game_type);
      setLegNumber(currentLeg);
      setTrackName(data.track_name);
      
      if (raceData && raceData.horses) {
        const newFlashing = new Set();
        data.race.horses.forEach(horse => {
          const prevHorse = raceData.horses.find(h => h.number === horse.number);
          if (prevHorse && prevHorse.odds !== horse.odds) {
            newFlashing.add(horse.number);
          }
        });
        if (newFlashing.size > 0) {
          setFlashingHorses(newFlashing);
          setTimeout(() => setFlashingHorses(new Set()), 500);
        }
      }
      
      setRaceData(data.race);
    } catch (error) {
      console.error("Failed to fetch race data:", error);
    }
  }, [currentGameId, currentLeg, raceData]);

  useEffect(() => {
    if (currentGameId) {
      fetchRaceData();
    }
  }, [currentGameId, currentLeg]);

  useEffect(() => {
    const interval = setInterval(fetchRaceData, 30000);
    return () => clearInterval(interval);
  }, [fetchRaceData]);

  const sortedHorses = raceData?.horses
    ?.sort((a, b) => b.distribution - a.distribution)
    ?.slice(0, 12) || [];

  if (!currentGameId || !raceData) {
    return (
      <div 
        style={{ 
          backgroundColor: '#020617', 
          minHeight: '100vh', 
          width: '100vw', 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#94A3B8',
          fontFamily: "'Barlow Condensed', sans-serif",
          fontSize: '1.5rem'
        }}
      >
        Venter på valg fra kontrollpanel...
      </div>
    );
  }

  return (
    <div 
      data-testid="fullscreen-view"
      style={{ 
        backgroundColor: '#020617', 
        minHeight: '100vh', 
        width: '100vw', 
        overflow: 'hidden',
        padding: '40px',
        boxSizing: 'border-box'
      }}
    >
      {/* Header */}
      <div 
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          marginBottom: '30px',
          borderBottom: '2px solid #334155',
          paddingBottom: '20px'
        }}
      >
        <div>
          <h1 
            style={{ 
              fontFamily: "'Barlow Condensed', sans-serif",
              fontSize: '4rem',
              fontWeight: 700,
              color: '#FFFFFF',
              margin: 0,
              textTransform: 'uppercase',
              letterSpacing: '0.02em'
            }}
          >
            {gameType}-{legNumber}
          </h1>
          <p 
            style={{ 
              fontFamily: "'Inter', sans-serif",
              fontSize: '1.5rem',
              color: '#94A3B8',
              margin: '8px 0 0 0',
              textTransform: 'uppercase',
              letterSpacing: '0.1em'
            }}
          >
            {trackName} • {raceData.distance}m
          </p>
        </div>
        <div 
          style={{
            textAlign: 'right'
          }}
        >
          <p 
            style={{ 
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '1rem',
              color: '#22C55E',
              margin: 0,
              textTransform: 'uppercase'
            }}
          >
            Direkteodds
          </p>
          <p 
            style={{ 
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '0.875rem',
              color: '#64748B',
              margin: '4px 0 0 0'
            }}
          >
            ATG.SE
          </p>
        </div>
      </div>

      {/* Odds Grid */}
      <div 
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '16px'
        }}
      >
        {sortedHorses.map((horse, index) => (
          <div
            key={horse.number}
            data-testid={`fullscreen-horse-${horse.number}`}
            className={flashingHorses.has(horse.number) ? 'flash-update' : ''}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '20px 24px',
              backgroundColor: index % 2 === 0 ? '#0F172A' : '#1E293B',
              borderRadius: '8px',
              border: '1px solid #334155'
            }}
          >
            {/* Horse Number */}
            <div
              style={{
                width: '56px',
                height: '56px',
                backgroundColor: '#FFFFFF',
                color: '#000000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: "'JetBrains Mono', monospace",
                fontWeight: 700,
                fontSize: '1.75rem',
                borderRadius: '8px',
                flexShrink: 0
              }}
            >
              {horse.number}
            </div>

            {/* Horse Info */}
            <div 
              style={{ 
                flex: 1, 
                marginLeft: '20px',
                overflow: 'hidden'
              }}
            >
              <div
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 600,
                  fontSize: '1.5rem',
                  color: '#FFFFFF',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {horse.name}
              </div>
              <div
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '1rem',
                  color: '#94A3B8',
                  marginTop: '4px'
                }}
              >
                {horse.driver}
              </div>
            </div>

            {/* Distribution */}
            <div
              style={{
                marginRight: '24px',
                textAlign: 'right'
              }}
            >
              <div
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '1.25rem',
                  color: '#22C55E',
                  fontWeight: 500
                }}
              >
                {horse.distribution.toFixed(1)}%
              </div>
              <div 
                style={{
                  width: '80px',
                  height: '4px',
                  backgroundColor: '#334155',
                  borderRadius: '2px',
                  marginTop: '6px',
                  overflow: 'hidden'
                }}
              >
                <div 
                  style={{
                    width: `${Math.min(horse.distribution, 100)}%`,
                    height: '100%',
                    backgroundColor: '#22C55E',
                    borderRadius: '2px'
                  }}
                />
              </div>
            </div>

            {/* Odds */}
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontWeight: 700,
                fontSize: '2rem',
                color: '#FFFFFF',
                textAlign: 'right',
                minWidth: '100px'
              }}
            >
              {horse.odds > 0 ? horse.odds.toFixed(2) : '-'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
